/**
 * The inbound feeds: which are delivering, what they threw away, and the one
 * control an operator gets over them.
 *
 * Three things here are safety features rather than status decoration.
 *
 * **The drop counters are the point.** A source silently discarding events
 * looks exactly like a quiet battlespace. An operator who can read "4,812
 * refused" can cut the feed and say so on the radio; one who cannot plans
 * around a picture with holes in it and never learns the holes were there.
 *
 * **Liveness is a fact with a time on it.** "Last heard 40s ago" is what makes
 * a feed that died distinguishable from a feed with nothing to say — the same
 * reason the map refuses to let a dead source render as two hundred contacts
 * independently going stale.
 *
 * **`may_assert_friend` is shown, because it silently rewrites the picture.**
 * A feed without it can send `a-f-*` all day and every one of those contacts
 * lands on the map as UNKNOWN. That is the correct behaviour and it is
 * invisible from the map alone, so it is stated here rather than left as a
 * discrepancy somebody eventually reports as a bug.
 *
 * The stop switch is operator-gated in this UI to match the server's
 * `require_operator`. The gate here is an affordance, not the enforcement:
 * `routers/external.py` checks again and is the thing that actually decides.
 *
 * There is no control for *where* a feed listens, and there cannot be. Source
 * addresses live in the environment, held by whoever holds the box — a route
 * that accepted a URL would be a server-side request forgery primitive on a
 * machine whose instance metadata endpoint sits at 169.254.169.254.
 */

import { useEffect, useState, type ReactNode } from 'react';

import {
  pinCorrelation,
  splitCorrelation,
  type ExternalSource,
  type ExternalTrack,
  type TrackContradiction,
} from '@/api/external';
import {
  externalActions,
  useExternalConfigured,
  useExternalSources,
  useExternalSwitchBusy,
  useExternalContradictions,
  useExternalMerges,
  useExternalTracks,
  type MergeGroup,
} from '@/stores/externalStore';
import { useHasRole } from '@/stores/authStore';

/** Slow-moving relative to the tracks themselves; a feed does not flap at 4 Hz. */
const SOURCES_POLL_MS = 5000;

type Liveness = 'live' | 'offline' | 'cut' | 'unknown';

/**
 * Whether a feed is delivering, as four distinct answers.
 *
 * A feed an operator deliberately cut and a feed that fell over are the same
 * absence of data and completely different things to do about it, so they are
 * never collapsed. "Never reported" is its own answer too: a source with no
 * health record has not been heard from, which is not the same as one that was
 * heard from and stopped.
 */
export function livenessOf(source: ExternalSource): Liveness {
  if (!source.enabled) return 'cut';
  if (source.health === null) return 'unknown';
  return source.health.state === 'live' ? 'live' : 'offline';
}

const LIVENESS_TEXT: Record<Liveness, string> = {
  live: 'LIVE',
  offline: 'OFFLINE',
  cut: 'CUT',
  unknown: 'NEVER REPORTED',
};

const LIVENESS_COLOR: Record<Liveness, string> = {
  live: 'var(--ok)',
  offline: 'var(--danger-foreground)',
  cut: 'var(--muted-foreground-3)',
  unknown: 'var(--caution)',
};

function ageText(unixSeconds: number | null, now: number): string {
  if (unixSeconds === null) return 'never';
  const seconds = Math.max(0, Math.round(now - unixSeconds));
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  return `${Math.floor(seconds / 3600)}h ago`;
}

/**
 * Everything one feed threw away, in the order an operator cares about.
 *
 * Zeroes are kept rather than filtered out. "0 refused" is a claim worth
 * reading; a missing row is indistinguishable from a counter nobody wired up.
 */
export function dropLines(source: ExternalSource): Array<[string, number]> {
  const counters = source.counters;
  if (counters === null) return [];
  return [
    ['DROPPED', counters.payloadsDropped],
    ['RATE LIMITED', counters.payloadsRateLimited],
    ['REFUSED', counters.observationsRefused],
    ['OVER CAP', counters.tracksRefusedOverCap],
  ];
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="well flex flex-col gap-0.5 rounded-[9px] px-2 py-1.5">
      <span className="label-mono text-[7.5px] text-[color:var(--muted-foreground-2)]">{label}</span>
      <span className="tnum text-[11px]" style={tone ? { color: tone } : undefined}>
        {value}
      </span>
    </div>
  );
}

function SourceCard({ source, now }: { source: ExternalSource; now: number }) {
  const canOperate = useHasRole('operator');
  const busy = useExternalSwitchBusy();
  const liveness = livenessOf(source);
  const drops = dropLines(source);
  const totalDropped = drops.reduce((sum, [, value]) => sum + value, 0);

  return (
    <div className="glass-2 flex flex-col gap-2 rounded-[13px] p-2.5">
      <div className="flex items-center gap-2">
        <span className="label-mono text-[9.5px]">{source.name || source.sourceId}</span>
        <span className="label-mono text-[8px] text-[color:var(--muted-foreground-3)]">
          {source.kind.toUpperCase()}
        </span>
        <span
          className="ml-auto label-mono text-[8px]"
          style={{ color: LIVENESS_COLOR[liveness] }}
        >
          ● {LIVENESS_TEXT[liveness]}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-1.5">
        <Stat label="LAST HEARD" value={ageText(source.health?.lastSeenAt ?? null, now)} />
        <Stat label="WRITTEN" value={String(source.counters?.observationsWritten ?? 0)} />
        <Stat
          label="DISCARDED"
          value={String(totalDropped)}
          // A non-zero total is the number that decides whether this feed can
          // be planned around at all, so it is coloured rather than left to be
          // spotted among four other figures.
          tone={totalDropped > 0 ? 'var(--caution)' : undefined}
        />
      </div>

      {drops.length > 0 ? (
        <div className="flex flex-wrap gap-x-3 gap-y-0.5">
          {drops.map(([label, value]) => (
            <span
              key={label}
              className="label-mono text-[8px]"
              style={{ color: value > 0 ? 'var(--caution)' : 'var(--muted-foreground-3)' }}
            >
              {label} {value}
            </span>
          ))}
        </div>
      ) : null}

      {source.counters?.lastRefusal ? (
        <p className="text-[9.5px] leading-snug" style={{ color: 'var(--caution)' }}>
          Last refusal: {source.counters.lastRefusal}
        </p>
      ) : null}
      {source.health?.lastError ? (
        <p className="text-[9.5px] leading-snug" style={{ color: 'var(--danger-foreground)' }}>
          {source.health.lastError} · {ageText(source.health.lastErrorAt, now)}
        </p>
      ) : null}

      {!source.mayAssertFriend ? (
        <p className="text-[9px] leading-snug text-[color:var(--muted-foreground-3)]">
          Not trusted to call anything friendly — every contact this feed marks as a friend is drawn
          UNKNOWN.
        </p>
      ) : null}

      <div className="flex items-center gap-2">
        <span className="label-mono text-[8px] text-[color:var(--muted-foreground-3)]">
          {source.enabled ? 'DELIVERING' : 'STOPPED'}
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={source.enabled}
          aria-label={`${source.enabled ? 'Cut' : 'Resume'} feed ${source.sourceId}`}
          disabled={!canOperate || busy === source.sourceId}
          onClick={() => void externalActions.setSourceEnabled(source.sourceId, !source.enabled)}
          className="hit-44 relative ml-auto h-[18px] w-8 shrink-0 rounded-[99px] transition-colors disabled:opacity-40"
          style={{ background: source.enabled ? 'var(--friend)' : 'oklch(1 0 0 / 0.15)' }}
        >
          <span
            className="absolute top-[2px] size-[14px] rounded-full bg-white transition-all"
            style={{ left: source.enabled ? 16 : 2 }}
          />
        </button>
      </div>
      {!canOperate ? (
        <p className="text-[9px] text-[color:var(--muted-foreground-3)]">
          Your role can watch this feed but not cut it. Ask an administrator for operator access.
        </p>
      ) : null}
    </div>
  );
}

/**
 * The feed roster. Rendered under the outbound CoT status, because inbound and
 * outbound are the two halves of the same connector and an operator asking
 * "what is this map made of" should not have to find two screens.
 */
/**
 * Feeds that coincide on a contact and disagree about what it is.
 *
 * Loud, and above the feed list, because it is the one thing on this panel
 * that is about the world rather than about the plumbing: two systems are
 * looking at the same point and one of them is wrong about whose it is. The
 * correlator refuses to pick a winner, and that refusal only buys anything if
 * somebody is told — a disagreement resolved into a single confident marker is
 * the finding deleted.
 */
function Contradictions({ clashes }: { clashes: TrackContradiction[] }): ReactNode {
  if (clashes.length === 0) return null;
  return (
    <div
      className="rounded-[12px] border p-2.5"
      style={{
        background: 'color-mix(in oklch, var(--danger-foreground) 9%, transparent)',
        borderColor: 'color-mix(in oklch, var(--danger-foreground) 40%, transparent)',
      }}
    >
      <div className="flex items-center gap-2">
        <span className="label-mono text-[9px]" style={{ color: 'var(--danger-foreground)' }}>
          FEEDS DISAGREE
        </span>
        <span className="ml-auto label-mono text-[8px] text-[color:var(--muted-foreground-3)]">
          {clashes.length} NOT MERGED
        </span>
      </div>
      <div className="mt-2 flex flex-col gap-1.5">
        {clashes.map((clash) => (
          <p
            key={`${clash.aTrackId}-${clash.bTrackId}-${clash.field}`}
            className="text-[10.5px] leading-relaxed text-[color:var(--muted-foreground)]"
          >
            <span className="label-mono">{clash.aSourceId}</span> says{' '}
            <span className="label-mono">{clash.aValue}</span>,{' '}
            <span className="label-mono">{clash.bSourceId}</span> says{' '}
            <span className="label-mono">{clash.bValue}</span> — {clash.field}, about contacts{' '}
            {clash.separationM.toFixed(0)} m apart. Left as two markers on purpose.
          </p>
        ))}
      </div>
    </div>
  );
}

/**
 * What the picture has merged, and the way to disagree with it.
 *
 * The map draws a merged contact as one marker, which is the point — but the
 * operator who can see two airframes needs somewhere to say so. Splitting is
 * stored server-side and fed back into every later pass, so it holds instead
 * of being undone by the next poll.
 *
 * Contributors are listed by id rather than only by callsign because the
 * callsigns are frequently different — that two feeds disagree about the name
 * is often the reason somebody is looking at this at all.
 */
function Merges({ groups }: { groups: MergeGroup[] }): ReactNode {
  const canSplit = useHasRole('operator');
  const [busy, setBusy] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  if (groups.length === 0) return null;

  async function split(group: MergeGroup) {
    setBusy(group.mergeId);
    setFailed(null);
    const result = await splitCorrelation(group.trackIds);
    setBusy(null);
    if (!result.ok) {
      setFailed(result.error.message);
      return;
    }
    // Re-read rather than editing the list here: the server decides what the
    // picture is, and a local edit would be a second answer that could differ.
    await externalActions.loadTracks();
  }

  return (
    <div className="well rounded-[12px] p-2.5">
      <div className="flex items-center gap-2">
        <span className="label-mono text-[9px]">MERGED CONTACTS</span>
        <span className="ml-auto label-mono text-[8px] text-[color:var(--muted-foreground-3)]">
          {groups.length} SHOWN AS ONE
        </span>
      </div>
      <div className="mt-2 flex flex-col gap-2">
        {groups.map((group) => (
          <div key={group.mergeId} className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-[10.5px] text-[color:var(--muted-foreground)]">
                {group.callsigns.length > 0 ? group.callsigns.join(' · ') : 'unnamed'}
              </p>
              <p className="label-mono text-[8.5px] text-[color:var(--muted-foreground-3)]">
                {group.trackIds.join('  ')}
              </p>
            </div>
            {canSplit ? (
              <button
                type="button"
                disabled={busy === group.mergeId}
                onClick={() => void split(group)}
                className="shrink-0 rounded-[8px] border px-2 py-1 label-mono text-[9px] disabled:opacity-40"
                style={{ borderColor: 'var(--border-nested)' }}
                title="These are not one object. The split is stored and survives later correlation passes."
              >
                {busy === group.mergeId ? 'SPLITTING…' : 'SPLIT'}
              </button>
            ) : null}
          </div>
        ))}
      </div>
      {failed ? (
        <p className="mt-2 text-[10px]" style={{ color: 'var(--danger-foreground)' }}>
          {failed}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Declaring that two separate markers are one object.
 *
 * The dangerous direction, so the list is explicit rather than clever: every
 * unmerged contact with its feed named, tick the ones that are the same
 * aircraft, and the button says how many will become one. A wrong pin deletes
 * a contact from everybody's map, which is not something to hide behind a
 * drag-and-drop.
 *
 * Only unmerged contacts are offered. Something already merged is changed by
 * splitting it, not by pinning it to a third thing.
 */
function PinContacts({ tracks }: { tracks: ExternalTrack[] }): ReactNode {
  const canPin = useHasRole('operator');
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  const loose = tracks.filter((track) => track.mergedInto === null);
  if (!canPin || loose.length < 2) return null;

  function toggle(trackId: string) {
    setPicked((prev) =>
      prev.includes(trackId) ? prev.filter((id) => id !== trackId) : [...prev, trackId],
    );
  }

  async function pin() {
    setBusy(true);
    setFailed(null);
    const result = await pinCorrelation(picked);
    setBusy(false);
    if (!result.ok) {
      setFailed(result.error.message);
      return;
    }
    setPicked([]);
    // Re-read: the server decides what the picture is. It may well refuse to
    // merge these after all — a contradiction between the two feeds survives a
    // pin — and inventing the merged marker here would show the operator a
    // result the server did not agree to.
    await externalActions.loadTracks();
  }

  return (
    <div className="well rounded-[12px] p-2.5">
      <div className="flex items-center gap-2">
        <span className="label-mono text-[9px]">SAME CONTACT?</span>
        <span className="ml-auto label-mono text-[8px] text-[color:var(--muted-foreground-3)]">
          {loose.length} SEPARATE
        </span>
      </div>
      <div className="scroll-thin mt-2 flex max-h-[132px] flex-col gap-1 overflow-y-auto">
        {loose.map((track) => (
          <label key={track.trackId} className="flex items-center gap-2 text-[10.5px]">
            <input
              type="checkbox"
              checked={picked.includes(track.trackId)}
              onChange={() => toggle(track.trackId)}
              aria-label={`Same contact as others: ${track.trackId}`}
            />
            <span className="min-w-0 truncate" style={{ color: 'var(--muted-foreground)' }}>
              {track.callsign || 'unnamed'}
            </span>
            <span className="label-mono ml-auto shrink-0 text-[8.5px] text-[color:var(--muted-foreground-3)]">
              {track.source.sourceId}
            </span>
          </label>
        ))}
      </div>
      <button
        type="button"
        disabled={picked.length < 2 || busy}
        onClick={() => void pin()}
        className="mt-2 rounded-[8px] border px-2.5 py-1 label-mono text-[9px] disabled:opacity-40"
        style={{ borderColor: 'var(--border-nested)' }}
        title="These are one object. Overrules the geometry and the identifiers, but not two feeds disagreeing about whose it is."
      >
        {busy ? 'PINNING…' : `PIN ${picked.length || ''} AS ONE`.replace('  ', ' ')}
      </button>
      {failed ? (
        <p className="mt-2 text-[10px]" style={{ color: 'var(--danger-foreground)' }}>
          {failed}
        </p>
      ) : null}
    </div>
  );
}

export function ExternalSourcesPanel(): ReactNode {
  const sources = useExternalSources();
  const configured = useExternalConfigured();
  const clashes = useExternalContradictions();
  const merges = useExternalMerges();
  const tracks = useExternalTracks();
  const now = Date.now() / 1000;

  useEffect(() => {
    const load = (): void => {
      void externalActions.loadSources();
    };
    load();
    const timer = setInterval(load, SOURCES_POLL_MS);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="flex flex-col gap-2.5 py-1">
      <div className="flex items-center gap-2">
        <span className="label-mono text-[9px]">INBOUND FEEDS</span>
        <span className="ml-auto label-mono text-[8px] text-[color:var(--muted-foreground-3)]">
          {sources.length} CONFIGURED
        </span>
      </div>

      <Contradictions clashes={clashes} />
      <Merges groups={merges} />
      <PinContacts tracks={tracks} />

      {!configured || sources.length === 0 ? (
        <div className="well rounded-[12px] p-3">
          <p className="text-[10.5px] leading-relaxed text-[color:var(--muted-foreground)]">
            No ingest is configured on this server. Feeds are declared in the deployment
            environment, not here — where data is allowed to come from is a decision for whoever
            holds the box. Once a CoT listener is configured its contacts appear on the map with the
            feed that carried them named on every one.
          </p>
        </div>
      ) : (
        sources.map((source) => <SourceCard key={source.sourceId} source={source} now={now} />)
      )}

      <p className="text-[9px] leading-relaxed text-[color:var(--muted-foreground-3)]">
        Cutting a feed removes its contacts from the live picture immediately rather than letting
        them age out. Addresses and credentials are not shown and cannot be set here.
      </p>
    </div>
  );
}

export default ExternalSourcesPanel;
